<template>
  <div class="container-details-main">
    <!-- Header with container info -->
    <VCard class="mb-6 container-details-header">
      <VCardText class="pa-4">
        <div class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <VBtn
              icon
              variant="text"
              color="primary"
              class="me-3 back-button"
              @click="goBack"
            >
              <VIcon icon="tabler-arrow-left" />
            </VBtn>
              
            <VAvatar
              :color="container.transport_type === 'air' ? 'info' : 'primary'"
              variant="tonal"
              size="48"
              class="me-3 container-avatar"
            >
              <VIcon
                :icon="container.transport_type === 'air' ? 'tabler-plane' : 'tabler-ship'"
                size="24"
              />
            </VAvatar>
              
            <div class="container-header-info">
              <h2 class="text-h5 mb-1 container-number">
                {{ container.container_number || 'Loading...' }}
              </h2>
              <div class="d-flex align-center gap-2">
                <VChip
                  :color="container.transport_type === 'air' ? 'info' : 'primary'"
                  size="small"
                  class="transport-type-chip"
                >
                  {{ container.transport_type === 'air' ? $t('shipment.air') : $t('shipment.sea') }}
                </VChip>
                <VChip
                  color="success"
                  size="small"
                  prepend-icon="tabler-circle-check"
                  class="status-chip"
                >
                  {{ $t('shipment.statuses.completed') }}
                </VChip>
              </div>
            </div>
          </div>
            
          <div class="d-flex gap-2 container-header-actions">
            <VBtn
              color="secondary"
              variant="outlined"
              prepend-icon="tabler-file-analytics"
              class="generate-report-button"
              @click="generateReport"
            >
              {{ $t('history.containers.container_report') }}
            </VBtn>
              
            <VBtn
              color="primary"
              variant="outlined"
              prepend-icon="tabler-download"
              class="export-data-button"
              @click="exportContainerData"
            >
              {{ $t('history.containers.export_container_data') }}
            </VBtn>
          </div>
        </div>
      </VCardText>
    </VCard>
  
    <VRow>
      <!-- General Information -->
      <VCol
        cols="12"
        lg="8"
      >
        <VCard class="mb-4 general-info-card">
          <VCardTitle class="d-flex align-center pa-4">
            <VIcon
              icon="tabler-info-circle"
              class="me-3"
            />
            {{ $t('history.containers.general_info') }}
          </VCardTitle>
            
          <VCardText class="pa-4">
            <VRow>
              <VCol
                cols="12"
                md="6"
              >
                <VList
                  density="compact"
                  class="info-list"
                >
                  <VListItem class="px-0">
                    <template #prepend>
                      <VIcon
                        color="primary"
                        size="20"
                      >
                        tabler-map-pin
                      </VIcon>
                    </template>
                    <VListItemTitle class="text-sm">
                      {{ $t('shipment.origin') }}
                    </VListItemTitle>
                    <VListItemSubtitle>{{ container.origin || '-' }}</VListItemSubtitle>
                  </VListItem>
                    
                  <VListItem class="px-0">
                    <template #prepend>
                      <VIcon
                        color="primary"
                        size="20"
                      >
                        tabler-calendar-event
                      </VIcon>
                    </template>
                    <VListItemTitle class="text-sm">
                      {{ $t('history.containers.arrived_date') }}
                    </VListItemTitle>
                    <VListItemSubtitle>{{ formatDate(container.arrival_date) }}</VListItemSubtitle>
                  </VListItem>
                    
                  <VListItem class="px-0">
                    <template #prepend>
                      <VIcon
                        color="success"
                        size="20"
                      >
                        tabler-calendar-check
                      </VIcon>
                    </template>
                    <VListItemTitle class="text-sm">
                      {{ $t('history.containers.completed_date') }}
                    </VListItemTitle>
                    <VListItemSubtitle>{{ formatDate(container.completed_date) }}</VListItemSubtitle>
                  </VListItem>
                </VList>
              </VCol>
                
              <VCol
                cols="12"
                md="6"
              >
                <VList
                  density="compact"
                  class="info-list"
                >
                  <VListItem class="px-0">
                    <template #prepend>
                      <VIcon
                        color="info"
                        size="20"
                      >
                        tabler-clock
                      </VIcon>
                    </template>
                    <VListItemTitle class="text-sm">
                      {{ $t('history.containers.processing_time') }}
                    </VListItemTitle>
                    <VListItemSubtitle>{{ container.processing_days || 0 }} {{ $t('history.containers.days') }}</VListItemSubtitle>
                  </VListItem>
                    
                  <VListItem class="px-0">
                    <template #prepend>
                      <VIcon
                        color="warning"
                        size="20"
                      >
                        tabler-clipboard-list
                      </VIcon>
                    </template>
                    <VListItemTitle class="text-sm">
                      {{ $t('shipment.packing_list') }}
                    </VListItemTitle>
                    <VListItemSubtitle>
                      <VChip
                        v-if="container.packing_list_id"
                        color="primary"
                        size="small"
                        :to="`/shipment/packing-lists/${container.packing_list_id}/view`"
                        class="packing-list-link"
                      >
                        #{{ container.packing_list_id }}
                      </VChip>
                      <span v-else>-</span>
                    </VListItemSubtitle>
                  </VListItem>
                    
                  <VListItem class="px-0">
                    <template #prepend>
                      <VIcon
                        color="secondary"
                        size="20"
                      >
                        tabler-notes
                      </VIcon>
                    </template>
                    <VListItemTitle class="text-sm">
                      {{ $t('shipment.notes') }}
                    </VListItemTitle>
                    <VListItemSubtitle>{{ container.notes || '-' }}</VListItemSubtitle>
                  </VListItem>
                </VList>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
  
        <!-- Container Timeline -->
        <VCard class="container-timeline-card">
          <VCardTitle class="d-flex align-center pa-4">
            <VIcon
              icon="tabler-timeline"
              class="me-3"
            />
            {{ $t('history.containers.container_timeline') }}
          </VCardTitle>
            
          <VCardText>
            <VTimeline
              density="compact"
              side="end"
              class="container-timeline"
            >
              <VTimelineItem
                v-for="event in timelineEvents"
                :key="event.id"
                :dot-color="event.color"
                size="small"
                class="timeline-item"
              >
                <template #icon>
                  <VIcon
                    :icon="event.icon"
                    size="16"
                  />
                </template>
                  
                <VCard
                  variant="outlined"
                  class="timeline-event-card"
                >
                  <VCardText>
                    <div class="d-flex justify-space-between align-center mb-2">
                      <h4 class="text-h6 event-title">
                        {{ event.title }}
                      </h4>
                      <span class="text-caption text-disabled event-date">{{ formatDateTime(event.date) }}</span>
                    </div>
                    <p class="text-sm mb-0 event-description">
                      {{ event.description }}
                    </p>
                  </VCardText>
                </VCard>
              </VTimelineItem>
            </VTimeline>
          </VCardText>
        </VCard>
      </VCol>
  
      <!-- Statistics Sidebar -->
      <VCol
        cols="12"
        lg="4"
      >
        <VCard class="mb-4 statistics-card">
          <VCardTitle class="d-flex align-center pa-4">
            <VIcon
              icon="tabler-chart-bar"
              class="me-3"
            />
            {{ $t('history.containers.statistics') }}
          </VCardTitle>
            
          <VCardText class="pa-4">
            <!-- Main Stats -->
            <div class="statistics-grid">
              <div class="stat-item text-center mb-3">
                <VAvatar size="40" color="primary" variant="tonal" class="mb-2">
                  <VIcon icon="tabler-package" size="20" />
                </VAvatar>
                <h4 class="text-h6 font-weight-bold mb-1 text-primary">
                  {{ container.parcels_count || 0 }}
                </h4>
                <p class="text-caption mb-0">
                  {{ $t('history.containers.parcels_processed') }}
                </p>
              </div>
                
              <div class="stat-item text-center mb-3">
                <VAvatar size="40" color="secondary" variant="tonal" class="mb-2">
                  <VIcon icon="tabler-users" size="20" />
                </VAvatar>
                <h4 class="text-h6 font-weight-bold mb-1 text-secondary">
                  {{ container.clients_count || 0 }}
                </h4>
                <p class="text-caption mb-0">
                  {{ $t('history.containers.clients_served') }}
                </p>
              </div>
                
              <div class="stat-item text-center mb-3">
                <VAvatar size="40" color="success" variant="tonal" class="mb-2">
                  <VIcon icon="tabler-weight" size="20" />
                </VAvatar>
                <h4 class="text-h6 font-weight-bold mb-1 text-success">
                  {{ formatWeight(container.total_weight || 0) }}
                </h4>
                <p class="text-caption mb-0">
                  {{ $t('history.containers.total_weight') }}
                </p>
              </div>
                
              <div class="stat-item text-center">
                <VAvatar size="40" color="info" variant="tonal" class="mb-2">
                  <VIcon icon="tabler-box" size="20" />
                </VAvatar>
                <h4 class="text-h6 font-weight-bold mb-1 text-info">
                  {{ formatVolume(container.total_volume || 0) }}
                </h4>
                <p class="text-caption mb-0">
                  {{ $t('history.containers.total_volume') }}
                </p>
              </div>
            </div>
          </VCardText>
        </VCard>
  
        <!-- Clients Served -->
        <VCard class="clients-served-card">
          <VCardTitle class="d-flex align-center justify-space-between pa-4">
            <div class="d-flex align-center">
              <VIcon
                icon="tabler-users"
                class="me-3"
              />
              {{ $t('history.containers.clients_served_details') }}
            </div>
            <VChip
              color="primary"
              size="small"
            >
              {{ clients.length }}
            </VChip>
          </VCardTitle>
            
          <VCardText>
            <div
              v-if="loadingClients"
              class="text-center py-4"
            >
              <VProgressCircular
                indeterminate
                color="primary"
                size="32"
              />
            </div>
              
            <div
              v-else-if="clients.length === 0"
              class="text-center py-4 no-clients-section"
            >
              <VIcon
                icon="tabler-users-off"
                size="48"
                class="mb-2 text-disabled"
              />
              <p class="text-subtitle-2 mb-0">
                {{ $t('shipment.no_clients_found') }}
              </p>
            </div>
              
            <VList
              v-else
              density="compact"
              class="clients-list"
            >
              <VListItem
                v-for="client in clients"
                :key="client.id"
                class="client-item"
                @click="viewClientHistory(client)"
              >
                <template #prepend>
                  <VAvatar
                    size="32"
                    color="primary"
                    variant="tonal"
                  >
                    <VIcon
                      icon="tabler-user"
                      size="16"
                    />
                  </VAvatar>
                </template>
                  
                <VListItemTitle class="client-name">
                  {{ client.name }}
                </VListItemTitle>
                <VListItemSubtitle class="client-stats">
                  {{ client.parcels_count || 0 }} {{ $t('shipment.parcels').toLowerCase() }} • 
                  {{ formatWeight(client.total_weight || 0) }}
                </VListItemSubtitle>
                  
                <template #append>
                  <VChip
                    :color="getClientStatusColor(client.status)"
                    size="small"
                    class="client-status-chip"
                  >
                    {{ $t(`shipment.statuses.${client.status}`, client.status) }}
                  </VChip>
                </template>
              </VListItem>
            </VList>
              
            <!-- View All Clients Button -->
            <div
              v-if="clients.length >= 5"
              class="text-center mt-4"
            >
              <VBtn
                color="primary"
                variant="outlined"
                size="small"
                prepend-icon="tabler-eye"
                class="view-all-clients-btn"
                @click="viewAllClients"
              >
                {{ $t('common.view_all') }}
              </VBtn>
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </div>
</template>
  
<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSnackbar } from '@/composables/useSnackbar'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
  
definePage({
  meta: {
    requiresAuth: true,
    role: ['admin', 'manager', 'user'],
  },
})
  
const { t } = useI18n()
const { showSnackbar } = useSnackbar()
const route = useRoute()
const router = useRouter()
  
// Props
const containerId = computed(() => route.params.containerId)
  
// Reactive data
const container = ref({})
const clients = ref([])
const timelineEvents = ref([])
const loadingContainer = ref(false)
const loadingClients = ref(false)
const loadingTimeline = ref(false)
  
// Methods
const fetchContainer = async () => {
  try {
    loadingContainer.value = true
      
    const response = await axios.get(`/shipment/history/containers/${containerId.value}`)

    container.value = response.data.data
  } catch (error) {
    console.error('Error fetching container:', error)
    showSnackbar({
      color: 'error',
      text: t('messages.fetch_failed', { item: 'container' }),
    })
  } finally {
    loadingContainer.value = false
  }
}
  
const fetchClients = async () => {
  try {
    loadingClients.value = true
      
    const response = await axios.get(`/shipment/history/containers/${containerId.value}/clients`)

    clients.value = response.data.data
  } catch (error) {
    console.error('Error fetching clients:', error)
    showSnackbar({
      color: 'error',
      text: t('messages.fetch_failed', { item: 'clients' }),
    })
  } finally {
    loadingClients.value = false
  }
}
  
const fetchTimeline = async () => {
  try {
    loadingTimeline.value = true
      
    const response = await axios.get(`/shipment/history/containers/${containerId.value}/timeline`)

    timelineEvents.value = response.data.data.map(event => ({
      ...event,
      color: getEventColor(event.type),
      icon: getEventIcon(event.type),
    }))
  } catch (error) {
    console.error('Error fetching timeline:', error)
    showSnackbar({
      color: 'error',
      text: t('messages.fetch_failed', { item: 'timeline' }),
    })
  } finally {
    loadingTimeline.value = false
  }
}
  
const formatDate = date => {
  if (!date) return '-'
  
  return new Date(date).toLocaleDateString()
}
  
const formatDateTime = date => {
  if (!date) return '-'
  
  return new Date(date).toLocaleString()
}
  
const formatWeight = weight => {
  const numWeight = parseFloat(weight) || 0
  return `${numWeight.toFixed(1)} kg`
}

const formatVolume = volume => {
  const numVolume = parseFloat(volume) || 0
  return `${numVolume.toFixed(2)} m³`
}
  
const getEventColor = eventType => {
  const eventColors = {
    arrival: 'info',
    customs: 'warning',
    processing: 'primary',
    completed: 'success',
    departure: 'secondary',
  }

  return eventColors[eventType] || 'default'
}
  
const getEventIcon = eventType => {
  const eventIcons = {
    arrival: 'tabler-truck',
    customs: 'tabler-shield-check',
    processing: 'tabler-tools',
    completed: 'tabler-circle-check',
    departure: 'tabler-plane-departure',
  }

  return eventIcons[eventType] || 'tabler-circle'
}
  
const getClientStatusColor = status => {
  const statusColors = {
    completed: 'success',
    delivered: 'success',
    in_process: 'warning',
    pending: 'info',
  }

  return statusColors[status] || 'default'
}
  
const goBack = () => {
  router.push({ name: 'history-containers' })
}
  
const generateReport = async () => {
  try {
    const response = await axios.get(`/shipment/history/containers/${containerId.value}/report`, {
      responseType: 'blob',
    })
      
    const blob = new Blob([response.data], { type: 'application/pdf' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.setAttribute('download', `container-${containerId.value}-report.pdf`)
    document.body.appendChild(link)
    link.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(link)
      
    showSnackbar({
      color: 'success',
      text: t('messages.report_generated'),
    })
  } catch (error) {
    console.error('Error generating report:', error)
    showSnackbar({
      color: 'error',
      text: t('messages.report_generation_failed'),
    })
  }
}
  
const exportContainerData = async () => {
  try {
    const response = await axios.get(`/shipment/history/containers/${containerId.value}/export`, {
      responseType: 'blob',
    })
      
    const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.setAttribute('download', `container-${containerId.value}-data.xlsx`)
    document.body.appendChild(link)
    link.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(link)
      
    showSnackbar({
      color: 'success',
      text: t('messages.export_successful'),
    })
  } catch (error) {
    console.error('Error exporting data:', error)
    showSnackbar({
      color: 'error',
      text: t('messages.export_failed'),
    })
  }
}
  
const viewClientHistory = client => {
  router.push({ name: 'history-customers-detail', params: { customerId: client.id } })
}
  
const viewAllClients = () => {
  router.push({ name: 'history-customers', query: { container: containerId.value } })
}
  
// Lifecycle
onMounted(() => {
  fetchContainer()
  fetchClients()
  fetchTimeline()
})

// Refetch when `:containerId` changes — same fix as on the parcel/customer
// detail pages. Cmd+K navigation to another container would otherwise
// leave the page showing the previous container's data.
watch(containerId, (newId, oldId) => {
  if (newId && newId !== oldId) {
    fetchContainer()
    fetchClients()
    fetchTimeline()
  }
})
</script>
  
<style scoped>
.container-details-main {
  font-family: 'Inter', 'Segoe UI', sans-serif;
}

.container-details-header {
  border: 1px solid rgb(var(--v-theme-surface-variant));
  background: rgb(var(--v-theme-surface));
}

.container-avatar {
  flex-shrink: 0;
}

.transport-type-chip,
.status-chip {
  font-size: 0.75rem;
}

.container-header-actions {
  flex-wrap: wrap;
  gap: 8px;
}

.general-info-card {
  border-left: 4px solid rgb(var(--v-theme-primary));
  border: 1px solid rgb(var(--v-theme-surface-variant));
}

.statistics-card {
  border-left: 4px solid rgb(var(--v-theme-secondary));
  border: 1px solid rgb(var(--v-theme-surface-variant));
}

.clients-served-card {
  border-left: 4px solid rgb(var(--v-theme-success));
  border: 1px solid rgb(var(--v-theme-surface-variant));
}

.container-timeline-card {
  border-left: 4px solid rgb(var(--v-theme-warning));
  border: 1px solid rgb(var(--v-theme-surface-variant));
}

.info-list .v-list-item {
  min-height: 40px;
  padding: 8px 0;
}

.statistics-grid {
  display: grid;
  gap: 12px;
}

.stat-item {
  padding: 12px;
  border-radius: 8px;
  border: 1px solid rgb(var(--v-theme-surface-variant));
  transition: all 0.2s ease-in-out;
}

.stat-item:hover {
  border-color: rgb(var(--v-theme-primary));
}

.timeline-event-card {
  margin-bottom: 8px;
}

.timeline-item {
  margin-bottom: 12px;
}

.client-item {
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  border-radius: 8px;
  margin-bottom: 4px;
}

.client-item:hover {
  background-color: rgb(var(--v-theme-surface-variant));
}

.clients-list {
  max-height: 300px;
  overflow-y: auto;
}

.no-clients-section {
  opacity: 0.7;
}

.packing-list-link {
  text-decoration: none;
}

@media (max-width: 768px) {
  .container-header-actions {
    flex-direction: column;
    align-items: stretch;
  }
  
  .container-header-actions .v-btn {
    margin-bottom: 8px;
  }
}
</style>